import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, AlertTriangle, Wallet, Users, Download, FileSpreadsheet } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CLASS_TYPES = ["All Classes", "Tigers Kung Fu", "Dragons Kung Fu", "Adults Kung Fu"];

export default function PaymentReports() {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Advanced export filters
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [exportFromMonth, setExportFromMonth] = useState("1");
  const [exportFromYear, setExportFromYear] = useState(currentYear.toString());
  const [exportToMonth, setExportToMonth] = useState(currentMonth.toString());
  const [exportToYear, setExportToYear] = useState(currentYear.toString());
  const [exportStatus, setExportStatus] = useState("all");
  const [exportClassType, setExportClassType] = useState("All Classes");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, paymentsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/payments/stats`),
        axios.get(`${API}/payments`),
        axios.get(`${API}/students`),
      ]);
      setStats(statsRes.data);
      setPayments(paymentsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error("Failed to load payment reports");
    } finally {
      setLoading(false);
    }
  };

  // Get student details by ID
  const getStudentDetails = (studentId) => {
    return students.find(s => s.id === studentId) || { name: "Unknown", class_type: "" };
  };

  // Generate CSV content
  const generateCSV = (data) => {
    const headers = ["Student Name", "Class Type", "Period", "Amount (£)", "Status", "Payment Date", "Date Paid"];
    const rows = data.map(p => {
      const student = getStudentDetails(p.student_id);
      const period = p.month && p.year ? `${MONTH_NAMES[p.month - 1]} ${p.year}` : p.date;
      return [
        student.name,
        student.class_type || "",
        period,
        p.amount.toFixed(2),
        p.status === "paid" ? "Paid" : "Unpaid",
        p.date || "",
        p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-GB') : ""
      ];
    });
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(","))
      .join("\n");
    
    return csvContent;
  };

  // Download CSV file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${filename}`);
  };

  // Simple export - export visible payment history (last 12 months)
  const handleQuickExport = () => {
    const dataToExport = payments.filter(p => p.month && p.year);
    if (dataToExport.length === 0) {
      toast.error("No payment data to export");
      return;
    }
    const csvContent = generateCSV(dataToExport);
    const date = new Date().toISOString().split('T')[0];
    downloadCSV(csvContent, `loongfu_payments_${date}.csv`);
  };

  // Advanced export with filters
  const handleAdvancedExport = () => {
    let filteredData = payments.filter(p => p.month && p.year);
    
    // Filter by date range
    const fromM = parseInt(exportFromMonth);
    const fromY = parseInt(exportFromYear);
    const toM = parseInt(exportToMonth);
    const toY = parseInt(exportToYear);
    
    filteredData = filteredData.filter(p => {
      const paymentDate = p.year * 12 + p.month;
      const fromDate = fromY * 12 + fromM;
      const toDate = toY * 12 + toM;
      return paymentDate >= fromDate && paymentDate <= toDate;
    });
    
    // Filter by status
    if (exportStatus !== "all") {
      filteredData = filteredData.filter(p => p.status === exportStatus);
    }
    
    // Filter by class type
    if (exportClassType !== "All Classes") {
      filteredData = filteredData.filter(p => {
        const student = getStudentDetails(p.student_id);
        return student.class_type === exportClassType;
      });
    }
    
    if (filteredData.length === 0) {
      toast.error("No payments match your filters");
      return;
    }
    
    const csvContent = generateCSV(filteredData);
    const filename = `loongfu_payments_${MONTH_NAMES[fromM-1]}${fromY}_to_${MONTH_NAMES[toM-1]}${toY}.csv`;
    downloadCSV(csvContent, filename);
    setExportDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#A1A1AA]">Loading...</div>
      </div>
    );
  }

  const currentMonthName = stats ? MONTH_NAMES[stats.current_month - 1] : "";

  // Get missed payments with student details
  const missedPayments = payments
    .filter((p) => p.status === "unpaid")
    .map((p) => {
      const student = students.find((s) => s.id === p.student_id);
      return {
        ...p,
        studentName: student?.name || "Unknown",
        studentClass: student?.class_type || "",
      };
    })
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });

  // Payment history by month
  const paymentHistory = [];
  const historyYear = stats?.current_year || new Date().getFullYear();
  const historyMonth = stats?.current_month || new Date().getMonth() + 1;
  
  for (let i = 0; i < 12; i++) {
    let m = historyMonth - i;
    let y = historyYear;
    if (m <= 0) {
      m += 12;
      y -= 1;
    }
    
    const monthPayments = payments.filter((p) => p.month === m && p.year === y);
    const paidPayments = monthPayments.filter((p) => p.status === "paid");
    const unpaidPayments = monthPayments.filter((p) => p.status === "unpaid");
    
    paymentHistory.push({
      month: m,
      year: y,
      label: `${MONTH_NAMES[m - 1]} ${y}`,
      totalPaid: paidPayments.reduce((sum, p) => sum + p.amount, 0),
      totalUnpaid: unpaidPayments.reduce((sum, p) => sum + p.amount, 0),
      paidCount: paidPayments.length,
      unpaidCount: unpaidPayments.length,
    });
  }

  return (
    <div data-testid="payment-reports-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold text-white uppercase tracking-wide"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          Class Payment Reports 課程付款報告
        </h1>
        <p className="text-[#A1A1AA] mt-1">Financial overview and income tracking</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#18181b] border border-white/10 p-1">
          <TabsTrigger 
            value="overview" 
            data-testid="tab-overview"
            className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="history"
            data-testid="tab-history"
            className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]"
          >
            Payment History
          </TabsTrigger>
          <TabsTrigger 
            value="missed"
            data-testid="tab-missed"
            className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]"
          >
            Missed Payments
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Total Income Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Monthly</p>
                    <p 
                      className="text-3xl font-bold text-white"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                      data-testid="monthly-income"
                    >
                      £{stats?.total_income?.monthly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="p-2 bg-[#27272a] rounded-sm">
                    <Wallet className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Quarterly</p>
                    <p 
                      className="text-3xl font-bold text-white"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                      data-testid="quarterly-income"
                    >
                      £{stats?.total_income?.quarterly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="p-2 bg-[#27272a] rounded-sm">
                    <TrendingUp className="w-5 h-5 text-[#22c55e]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Six Months</p>
                    <p 
                      className="text-3xl font-bold text-white"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                      data-testid="six-months-income"
                    >
                      £{stats?.total_income?.six_months?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="p-2 bg-[#27272a] rounded-sm">
                    <TrendingUp className="w-5 h-5 text-[#60A5FA]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2">Yearly</p>
                    <p 
                      className="text-3xl font-bold text-white"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                      data-testid="yearly-income"
                    >
                      £{stats?.total_income?.yearly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="p-2 bg-[#27272a] rounded-sm">
                    <TrendingUp className="w-5 h-5 text-[#D92323]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Kids vs Adults Income */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-6">
                <CardTitle 
                  className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <Users className="w-5 h-5 text-orange-400" />
                  Kids Classes Income
                </CardTitle>
                <p className="text-[#A1A1AA] text-sm mt-1">Tigers & Dragons Kung Fu</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Monthly</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.kids_income?.monthly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Quarterly</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.kids_income?.quarterly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Six Months</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.kids_income?.six_months?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Yearly</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.kids_income?.yearly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-6">
                <CardTitle 
                  className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  <Users className="w-5 h-5 text-red-500" />
                  Adults Classes Income
                </CardTitle>
                <p className="text-[#A1A1AA] text-sm mt-1">Adults Kung Fu</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Monthly</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.adults_income?.monthly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Quarterly</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.adults_income?.quarterly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Six Months</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.adults_income?.six_months?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs uppercase">Yearly</p>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      £{stats?.adults_income?.yearly?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Missed Payments Summary */}
          <Card className="bg-red-500/10 border-red-500/20 rounded-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-500/20 rounded-sm">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
                <div className="flex-1">
                  <h3 
                    className="text-lg font-semibold text-white uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                  >
                    Missed Payments This Month
                  </h3>
                  <p className="text-[#A1A1AA] text-sm">{currentMonthName} {stats?.current_year}</p>
                </div>
                <div className="text-right">
                  <p 
                    className="text-3xl font-bold text-red-500"
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                    data-testid="missed-payments-count"
                  >
                    {stats?.missed_payments?.count || 0}
                  </p>
                  <p className="text-[#A1A1AA] text-sm">
                    £{stats?.missed_payments?.amount?.toFixed(2) || "0.00"} outstanding
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle 
                  className="text-lg font-semibold text-white uppercase tracking-wide"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  Monthly Payment History
                </CardTitle>
                <div className="flex gap-2">
                  {/* Quick Export Button */}
                  <Button
                    onClick={handleQuickExport}
                    variant="outline"
                    size="sm"
                    data-testid="quick-export-btn"
                    className="border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Quick Export
                  </Button>
                  
                  {/* Advanced Export Dialog */}
                  <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="advanced-export-btn"
                        className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Advanced Export
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
                      <DialogHeader>
                        <DialogTitle 
                          className="text-xl font-bold uppercase tracking-wide"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          Export Payment Data
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        {/* Date Range */}
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">From</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={exportFromMonth} onValueChange={setExportFromMonth}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#27272a] border-white/10">
                                {MONTH_NAMES.map((name, idx) => (
                                  <SelectItem key={idx} value={(idx + 1).toString()} className="text-white hover:bg-white/10">
                                    {name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={exportFromYear} onValueChange={setExportFromYear}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#27272a] border-white/10">
                                {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((year) => (
                                  <SelectItem key={year} value={year.toString()} className="text-white hover:bg-white/10">
                                    {year}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">To</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={exportToMonth} onValueChange={setExportToMonth}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#27272a] border-white/10">
                                {MONTH_NAMES.map((name, idx) => (
                                  <SelectItem key={idx} value={(idx + 1).toString()} className="text-white hover:bg-white/10">
                                    {name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={exportToYear} onValueChange={setExportToYear}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#27272a] border-white/10">
                                {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((year) => (
                                  <SelectItem key={year} value={year.toString()} className="text-white hover:bg-white/10">
                                    {year}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Status Filter */}
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">Payment Status</Label>
                          <Select value={exportStatus} onValueChange={setExportStatus}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/10">
                              <SelectItem value="all" className="text-white hover:bg-white/10">All Payments</SelectItem>
                              <SelectItem value="paid" className="text-white hover:bg-white/10">Paid Only</SelectItem>
                              <SelectItem value="unpaid" className="text-white hover:bg-white/10">Unpaid Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Class Filter */}
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">Class Type</Label>
                          <Select value={exportClassType} onValueChange={setExportClassType}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/10">
                              {CLASS_TYPES.map((type) => (
                                <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <Button
                          onClick={handleAdvancedExport}
                          data-testid="confirm-export-btn"
                          className="w-full bg-[#D4AF37] hover:bg-[#b8972e] text-black font-semibold uppercase tracking-wider"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Export to CSV
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Period</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Paid</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Unpaid</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Total Received</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Outstanding</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentHistory.map((row) => (
                      <TableRow 
                        key={`${row.month}-${row.year}`}
                        className="border-white/5 hover:bg-white/5"
                      >
                        <TableCell className="font-medium text-white">{row.label}</TableCell>
                        <TableCell className="text-[#22c55e]">{row.paidCount}</TableCell>
                        <TableCell className="text-[#ef4444]">{row.unpaidCount}</TableCell>
                        <TableCell className="text-white font-mono">£{row.totalPaid.toFixed(2)}</TableCell>
                        <TableCell className="text-red-400 font-mono">£{row.totalUnpaid.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Missed Payments Tab */}
        <TabsContent value="missed" className="space-y-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardHeader className="border-b border-white/5 p-6">
              <CardTitle 
                className="text-lg font-semibold text-white uppercase tracking-wide"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                All Missed Payments
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Student</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Class</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Period</TableHead>
                      <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {missedPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-[#A1A1AA]">
                          No missed payments - Great job! 🎉
                        </TableCell>
                      </TableRow>
                    ) : (
                      missedPayments.map((payment) => (
                        <TableRow 
                          key={payment.id}
                          className="border-white/5 hover:bg-white/5"
                        >
                          <TableCell className="font-medium text-white">{payment.studentName}</TableCell>
                          <TableCell className="text-[#A1A1AA]">{payment.studentClass}</TableCell>
                          <TableCell className="text-white">
                            {MONTH_NAMES[payment.month - 1]} {payment.year}
                          </TableCell>
                          <TableCell className="text-red-400 font-mono">£{payment.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
